import { createSlice } from '@reduxjs/toolkit';
import {
  activateStockSync,
  fetchCatalogRun,
  fetchListings,
  fetchStockPreview,
  fetchStockSync,
  fetchStockSyncChannels,
  fetchStoreLocations,
  fetchStoreSales,
  startCatalogSync,
  startStockPreview,
  syncListing,
  updateListing,
  updateStockSync,
} from './stockSyncThunks';
import { ALIGNMENT_FILTER, LISTING_FILTER, RUN_KIND, STORE_SALE_FILTER } from './stockSyncConstants';
import { previewSummaryOf, previewTokenOf } from './stockSyncUtils';

const initialListings = {
  rows: [],
  totalCount: 0,
  loading: false,
  error: null,
  /** Only the latest request may write: a watch reads the page again while a filter may be on its way. */
  requestId: null,
  filters: {
    search: '',
    status: LISTING_FILTER.ALL,
    page: 1,
    rowsPerPage: 20,
  },
};

const initialStoreSales = {
  rows: [],
  totalCount: 0,
  loading: false,
  error: null,
  requestId: null,
  filters: {
    search: '',
    status: STORE_SALE_FILTER.ALL,
    page: 1,
    rowsPerPage: 20,
  },
};

const initialAlignment = {
  /** The comparison in use, rows and all: `detail.runs` keeps only its summary. */
  preview: null,
  loading: false,
  error: null,
  /** The token of a comparison whose hour went by. */
  expiredToken: null,
  filters: {
    search: '',
    state: ALIGNMENT_FILTER.ALL,
    page: 1,
    rowsPerPage: 50,
  },
};

const initialState = {
  /** The stores in reach of the page: what draws its «Sincronizar con Shopify». */
  channels: [],
  channelsLoaded: false,
  channelsRequestId: null,

  /** The store the dialogs and the products page work on, and how its sync stands. */
  selectedId: null,
  detail: null,
  detailLoading: false,
  detailError: null,

  /** The store's own locations, read from Shopify when a dialog needs them. */
  locations: [],
  locationsLoading: false,
  locationsError: null,

  /** The page watching the store's runs and products (`STOCK_SYNC_WATCHER`). */
  watchedBy: null,

  listings: initialListings,

  /** The store's own sales whose order never reached Ninja, a page at a time. */
  storeSales: initialStoreSales,

  alignment: initialAlignment,
};

const settingIdOf = (action) => action.meta.arg?.settingId;
const isBackground = (action) => action.meta.arg?.background === true;

/** The store's row as the server just answered it, in both places it is kept. */
const storeDetail = (state, detail) => {
  if (detail?.channel?.id !== state.selectedId) return;
  state.detail = detail;
  state.detailLoading = false;
  state.detailError = null;
  const index = state.channels.findIndex((row) => row.channel.id === detail.channel.id);
  if (index !== -1) state.channels[index] = detail;
};

const storeRun = (kind) => (state, action) => {
  if (settingIdOf(action) !== state.selectedId || !state.detail) return;
  state.detail.runs = { ...state.detail.runs, [kind]: action.payload ?? null };
};

const storeListing = (state, action) => {
  const index = state.listings.rows.findIndex((row) => row.id === action.payload?.id);
  if (index !== -1) state.listings.rows[index] = action.payload;
};

/**
 * A comparison as the server answered it: whole for the review, its summary
 * for the store's runs. Another token means another comparison, seen from
 * its first page.
 */
const storePreview = (state, action) => {
  if (settingIdOf(action) !== state.selectedId) return;
  const report = action.payload ?? null;
  const { alignment } = state;
  if (previewTokenOf(report) !== previewTokenOf(alignment.preview)) {
    alignment.expiredToken = null;
    alignment.filters.page = 1;
  }
  alignment.preview = report;
  alignment.loading = false;
  alignment.error = null;
  if (state.detail) state.detail.runs = { ...state.detail.runs, [RUN_KIND.STOCK_PREVIEW]: previewSummaryOf(report) };
};

const stockSyncSlice = createSlice({
  name: 'stockSync',
  initialState,
  reducers: {
    /** Another store, or none: what was read of the previous one goes. */
    stockSyncChannelSelected: (state, action) => {
      const settingId = action.payload ?? null;
      if (settingId === state.selectedId) return;
      state.selectedId = settingId;
      state.detail = null;
      state.detailLoading = false;
      state.detailError = null;
      state.locations = [];
      state.locationsLoading = false;
      state.locationsError = null;
      state.listings = { ...initialListings, filters: { ...state.listings.filters, page: 1 } };
      state.storeSales = { ...initialStoreSales, filters: { ...state.storeSales.filters, page: 1 } };
      state.alignment = initialAlignment;
    },
    stockSyncWatched: (state, action) => {
      state.watchedBy = action.payload;
    },
    /** Only the page that is watching stops the watches: the next one may have mounted first. */
    stockSyncLeft: (state, action) => {
      if (state.watchedBy === action.payload) state.watchedBy = null;
    },
    /** Merges a patch into the filters; anything but a page change goes back to page 1. */
    setListingsFilters: (state, action) => {
      state.listings.filters = { ...state.listings.filters, page: 1, ...action.payload };
    },
    setStoreSalesFilters: (state, action) => {
      state.storeSales.filters = { ...state.storeSales.filters, page: 1, ...action.payload };
    },
    setAlignmentFilters: (state, action) => {
      state.alignment.filters = { ...state.alignment.filters, page: 1, ...action.payload };
    },
    /** The token whose hour went by; a newer comparison has another one. */
    stockPreviewExpired: (state, action) => {
      state.alignment.expiredToken = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStockSyncChannels.pending, (state, action) => {
        state.channelsRequestId = action.meta.requestId;
      })
      .addCase(fetchStockSyncChannels.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.channelsRequestId) return;
        // Still none: the pages that only ask whether there are stores keep their render.
        if (action.payload.length > 0 || state.channels.length > 0) state.channels = action.payload;
        state.channelsLoaded = true;
      })
      // Without the list there is nothing to offer: the option is not drawn.
      .addCase(fetchStockSyncChannels.rejected, (state, action) => {
        if (action.meta.requestId !== state.channelsRequestId) return;
        state.channels = [];
        state.channelsLoaded = true;
      })

      .addCase(fetchStockSync.pending, (state, action) => {
        if (settingIdOf(action) !== state.selectedId || isBackground(action)) return;
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchStockSync.fulfilled, (state, action) => storeDetail(state, action.payload))
      .addCase(fetchStockSync.rejected, (state, action) => {
        if (settingIdOf(action) !== state.selectedId || isBackground(action)) return;
        state.detailLoading = false;
        state.detailError = action.payload;
      })
      .addCase(updateStockSync.fulfilled, (state, action) => storeDetail(state, action.payload))

      .addCase(fetchStoreLocations.pending, (state, action) => {
        if (settingIdOf(action) !== state.selectedId) return;
        state.locationsLoading = true;
        state.locationsError = null;
      })
      .addCase(fetchStoreLocations.fulfilled, (state, action) => {
        if (settingIdOf(action) !== state.selectedId) return;
        state.locationsLoading = false;
        state.locations = action.payload;
      })
      .addCase(fetchStoreLocations.rejected, (state, action) => {
        if (settingIdOf(action) !== state.selectedId) return;
        state.locationsLoading = false;
        state.locations = [];
        state.locationsError = action.payload;
      })

      .addCase(startCatalogSync.fulfilled, storeRun(RUN_KIND.CATALOG))
      .addCase(fetchCatalogRun.fulfilled, storeRun(RUN_KIND.CATALOG))
      // The answer is the store's sync, its publication on the way; the comparison is spent.
      .addCase(activateStockSync.fulfilled, (state, action) => {
        storeDetail(state, action.payload);
        const { preview } = state.alignment;
        if (preview && previewTokenOf(preview) === action.meta.arg.token) {
          preview.applied_at =
            action.payload?.runs?.[RUN_KIND.STOCK_PREVIEW]?.applied_at || action.payload?.activated_at || null;
        }
      })

      .addCase(fetchStockPreview.pending, (state, action) => {
        if (settingIdOf(action) !== state.selectedId || isBackground(action)) return;
        state.alignment.loading = true;
        state.alignment.error = null;
      })
      .addCase(fetchStockPreview.fulfilled, storePreview)
      .addCase(fetchStockPreview.rejected, (state, action) => {
        if (settingIdOf(action) !== state.selectedId || isBackground(action)) return;
        state.alignment.loading = false;
        state.alignment.error = action.payload;
      })
      .addCase(startStockPreview.fulfilled, storePreview)

      .addCase(fetchListings.pending, (state, action) => {
        state.listings.requestId = action.meta.requestId;
        if (isBackground(action)) return;
        state.listings.loading = true;
        state.listings.error = null;
      })
      .addCase(fetchListings.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listings.requestId) return;
        if (action.payload.settingId !== state.selectedId) return;
        state.listings.loading = false;
        state.listings.rows = action.payload.results;
        state.listings.totalCount = action.payload.count;
      })
      .addCase(fetchListings.rejected, (state, action) => {
        if (action.meta.requestId !== state.listings.requestId) return;
        state.listings.loading = false;
        // A watch's read that fails leaves the page as it was.
        if (isBackground(action)) return;
        state.listings.error = action.payload;
        state.listings.rows = [];
        state.listings.totalCount = 0;
      })
      .addCase(updateListing.fulfilled, storeListing)
      .addCase(syncListing.fulfilled, storeListing)

      .addCase(fetchStoreSales.pending, (state, action) => {
        state.storeSales.requestId = action.meta.requestId;
        state.storeSales.loading = true;
        state.storeSales.error = null;
      })
      .addCase(fetchStoreSales.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.storeSales.requestId) return;
        if (action.payload.settingId !== state.selectedId) return;
        state.storeSales.loading = false;
        state.storeSales.rows = action.payload.results;
        state.storeSales.totalCount = action.payload.count;
      })
      .addCase(fetchStoreSales.rejected, (state, action) => {
        if (action.meta.requestId !== state.storeSales.requestId) return;
        state.storeSales.loading = false;
        state.storeSales.error = action.payload;
        state.storeSales.rows = [];
        state.storeSales.totalCount = 0;
      });
  },
});

export const {
  stockSyncChannelSelected,
  stockSyncWatched,
  stockSyncLeft,
  setListingsFilters,
  setStoreSalesFilters,
  setAlignmentFilters,
  stockPreviewExpired,
} = stockSyncSlice.actions;

export default stockSyncSlice.reducer;
